import {
  IconBrain as Brain,
  IconChecklist as Checklist,
  IconChevronDown as ChevronDown,
  IconChevronUp as ChevronUp,
  IconFolder as Folder,
  IconLayoutDashboard as LayoutDashboard,
  IconMessageCircle as MessageCircle,
  IconSettings as Settings,
  type Icon,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";

export type WorkspaceId = "overview" | "chat" | "memory" | "cron" | "files" | "more";

interface WorkspaceItem {
  id: WorkspaceId;
  label: string;
  icon: Icon;
}

interface WorkspaceDockProps {
  activeWorkspace: WorkspaceId;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  onSelect: (workspace: WorkspaceId) => boolean | void;
  /** hover、键盘聚焦或触屏按下已表达导航意图，可用于提前加载目标模块。 */
  onIntent?: (workspace: WorkspaceId) => void;
}

const DESKTOP_POINTER_QUERY = "(hover: hover) and (pointer: fine)";
const INITIAL_IDLE_DELAY_MS = 4_000;
const POINTER_LEAVE_DELAY_MS = 1_000;
const HOVER_EXPAND_DELAY_MS = 80;
const REVEAL_CLICK_GUARD_MS = 180;

const WORKSPACES: WorkspaceItem[] = [
  { id: "overview", label: "概览", icon: LayoutDashboard },
  { id: "chat", label: "会话", icon: MessageCircle },
  { id: "memory", label: "记忆", icon: Brain },
  { id: "cron", label: "任务", icon: Checklist },
  { id: "files", label: "文件", icon: Folder },
  { id: "more", label: "更多", icon: Settings },
];

/**
 * macOS 风格的双态 Dock。
 *
 * 只有具备精确指针与悬停能力的桌面设备才会自动收起。触屏设备始终保留完整
 * 导航，避免把关键入口藏在不存在的 hover 交互后面。
 */
export function WorkspaceDock({
  activeWorkspace,
  collapsed,
  onCollapsedChange,
  onSelect,
  onIntent,
}: WorkspaceDockProps) {
  const dockRef = useRef<HTMLElement>(null);
  const collapseTimer = useRef<number | null>(null);
  const expandTimer = useRef<number | null>(null);
  const revealGuardTimer = useRef<number | null>(null);
  const lastPointerPosition = useRef<{ x: number; y: number } | null>(null);
  const [desktopPointer, setDesktopPointer] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const activeItem = WORKSPACES.find((item) => item.id === activeWorkspace) ?? WORKSPACES[0]!;

  const clearCollapseTimer = useCallback(() => {
    if (collapseTimer.current !== null) window.clearTimeout(collapseTimer.current);
    collapseTimer.current = null;
  }, []);

  const clearExpandTimer = useCallback(() => {
    if (expandTimer.current !== null) window.clearTimeout(expandTimer.current);
    expandTimer.current = null;
  }, []);

  const clearRevealGuard = useCallback(() => {
    if (revealGuardTimer.current !== null) window.clearTimeout(revealGuardTimer.current);
    revealGuardTimer.current = null;
  }, []);

  const scheduleCollapse = useCallback(
    (delay: number) => {
      clearCollapseTimer();
      if (!desktopPointer || collapsed) return;
      collapseTimer.current = window.setTimeout(() => {
        if (dockRef.current?.contains(document.activeElement)) return;
        const rect = dockRef.current?.getBoundingClientRect();
        const pointer = lastPointerPosition.current;
        // 子节点在双态切换时会被替换，浏览器可能为旧节点派发一次伪 leave。
        // 计时结束后以 Dock 当前几何范围复核，指针仍在范围内就不收起。
        if (
          rect &&
          rect.width > 0 &&
          rect.height > 0 &&
          pointer &&
          pointer.x >= rect.left &&
          pointer.x <= rect.right &&
          pointer.y >= rect.top &&
          pointer.y <= rect.bottom
        ) {
          return;
        }
        onCollapsedChange(true);
      }, delay);
    },
    [clearCollapseTimer, collapsed, desktopPointer, onCollapsedChange],
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(DESKTOP_POINTER_QUERY);
    const syncPointerMode = () => {
      setDesktopPointer(media.matches);
      if (!media.matches) onCollapsedChange(false);
    };
    syncPointerMode();
    media.addEventListener("change", syncPointerMode);
    return () => media.removeEventListener("change", syncPointerMode);
  }, [onCollapsedChange]);

  useEffect(() => {
    if (desktopPointer && !collapsed) scheduleCollapse(INITIAL_IDLE_DELAY_MS);
    return clearCollapseTimer;
  }, [clearCollapseTimer, collapsed, desktopPointer, scheduleCollapse]);

  useEffect(
    () => () => {
      clearExpandTimer();
      clearRevealGuard();
    },
    [clearExpandTimer, clearRevealGuard],
  );

  const expandSafely = () => {
    clearExpandTimer();
    clearRevealGuard();
    clearCollapseTimer();
    if (!collapsed) return;
    // 悬停展开会改变鼠标下方的 DOM；短暂屏蔽入口，避免快速点击穿透到某个页面。
    setRevealing(true);
    onCollapsedChange(false);
    revealGuardTimer.current = window.setTimeout(() => {
      setRevealing(false);
      revealGuardTimer.current = null;
    }, REVEAL_CLICK_GUARD_MS);
  };

  const scheduleExpand = () => {
    clearCollapseTimer();
    clearExpandTimer();
    if (!collapsed) return;
    expandTimer.current = window.setTimeout(expandSafely, HOVER_EXPAND_DELAY_MS);
  };

  return (
    <nav
      ref={dockRef}
      className={`workspace-dock ${collapsed ? "workspace-dock--collapsed" : "workspace-dock--expanded"} ${revealing ? "workspace-dock--revealing" : ""}`}
      data-state={collapsed ? "collapsed" : "expanded"}
      aria-label="HerSkin 工作空间 Dock"
      onPointerEnter={(event) => {
        lastPointerPosition.current = { x: event.clientX, y: event.clientY };
        scheduleExpand();
      }}
      onPointerMove={(event) => {
        lastPointerPosition.current = { x: event.clientX, y: event.clientY };
      }}
      onPointerLeave={(event) => {
        lastPointerPosition.current = { x: event.clientX, y: event.clientY };
        clearExpandTimer();
        scheduleCollapse(POINTER_LEAVE_DELAY_MS);
      }}
      onFocusCapture={clearCollapseTimer}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          scheduleCollapse(POINTER_LEAVE_DELAY_MS);
        }
      }}
    >
      {collapsed ? (
        <button
          type="button"
          className="workspace-dock-wake"
          aria-label={`展开 Dock，当前页面：${activeItem.label}`}
          aria-expanded="false"
          onClick={expandSafely}
        >
          <activeItem.icon size={18} stroke={1.9} />
          <span>{activeItem.label}</span>
          <small>Dock</small>
          <ChevronUp size={15} stroke={1.9} aria-hidden="true" />
        </button>
      ) : (
        <>
          {WORKSPACES.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`workspace-dock-item ${activeWorkspace === item.id ? "active" : ""}`}
              aria-current={activeWorkspace === item.id ? "page" : undefined}
              onPointerEnter={() => onIntent?.(item.id)}
              onPointerDown={() => onIntent?.(item.id)}
              onFocus={() => onIntent?.(item.id)}
              onClick={() => onSelect(item.id)}
            >
              <item.icon size={20} stroke={1.8} />
              <span>{item.label}</span>
            </button>
          ))}
          {desktopPointer ? (
            <button
              type="button"
              className="workspace-dock-collapse"
              aria-label="收起 Dock"
              aria-expanded="true"
              onClick={() => {
                setRevealing(false);
                onCollapsedChange(true);
              }}
            >
              <ChevronDown size={17} stroke={1.9} />
            </button>
          ) : null}
        </>
      )}
    </nav>
  );
}
